import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faClock, faArrowRight, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import "../../styles/app/CreateAgentCTA.css";

// Mixed order: alternating women and men for variety
const CTA_AGENTS = [
  { name: "Sofia", image: "/images/agent-avatar.png", role: "AI Receptionist" },
  { name: "Leo", image: "/images/agent-leo.png", role: "AI Sales Rep" },
  { name: "Luna", image: "/images/agent-luna.png", role: "AI Scheduler" },
  { name: "Max", image: "/images/agent-max.png", role: "AI Concierge" },
  { name: "Aria", image: "/images/agent-aria.png", role: "AI Support Agent" },
  { name: "Dan", image: "/images/agent-dan.png", role: "AI Closer" },
  { name: "Maya", image: "/images/agent-maya.png", role: "AI Receptionist" },
  { name: "Alex", image: "/images/agent-alex.png", role: "AI Advisor" },
  { name: "Zoe", image: "/images/agent-zoe.png", role: "AI Sales Rep" },
  { name: "Lexi", image: "/images/agent-lexi.png", role: "AI Scheduler" },
  { name: "Nova", image: "/images/agent-nova.png", role: "AI Concierge" },
  { name: "Iris", image: "/images/agent-iris.png", role: "AI Support Agent" },
];

const CreateAgentCTA: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const totalAgents = CTA_AGENTS.length;

  const getVisibleAgents = useCallback(() => {
    const prev = (activeIndex - 1 + totalAgents) % totalAgents;
    const next = (activeIndex + 1) % totalAgents;
    return [
      { ...CTA_AGENTS[prev], position: "side-left" },
      { ...CTA_AGENTS[activeIndex], position: "center" },
      { ...CTA_AGENTS[next], position: "side-right" },
    ];
  }, [activeIndex, totalAgents]);

  const goNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setActiveIndex((prev) => (prev + 1) % totalAgents);
    setTimeout(() => setIsTransitioning(false), 400);
  };

  const goPrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setActiveIndex((prev) => (prev - 1 + totalAgents) % totalAgents);
    setTimeout(() => setIsTransitioning(false), 400);
  };

  // Auto-rotate every 4 seconds
  useEffect(() => {
    const timer = setInterval(goNext, 4000);
    return () => clearInterval(timer);
  }, [activeIndex]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (websiteUrl.trim()) {
      navigate(`/create-agent?url=${encodeURIComponent(websiteUrl.trim())}&agent=${CTA_AGENTS[activeIndex].name.toLowerCase()}`);
    }
  };

  const visibleAgents = getVisibleAgents();

  return (
    <section className="create-agent-cta" id="create-agent">
      <div className="cta-inner">
        <div className="cta-text-col">
          <div className="cta-badge">
            <FontAwesomeIcon icon={faBolt} style={{ marginRight: 6 }} />
            {t("createAgentCTA.badge", "Ready in 3 Minutes")}
          </div>
          <h2 className="cta-headline">
            {t("createAgentCTA.headline1", "Create Your Own")} <br />
            <span className="gradient-text">
              {t("createAgentCTA.headline2", "AI Voice Agent")}
            </span>{" "}
            {t("createAgentCTA.headline3", "Now")}
          </h2>
          <p className="cta-description">
            {t(
              "createAgentCTA.description",
              "Enter your website and our AI will automatically learn about your business, generate an optimized prompt, and let you test your agent in minutes. No technical skills required."
            )}
          </p>

          <form className="cta-url-form" onSubmit={handleSubmit}>
            <input
              type="text"
              className="cta-url-input"
              placeholder={t("createAgentCTA.placeholder", "yourcompany.com")}
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              required
              id="cta-website-input"
            />
            <button type="submit" className="cta-submit-btn" id="cta-create-btn">
              {t("createAgentCTA.button", "Create")} <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 6 }} />
            </button>
          </form>

          <div className="cta-trust-row">
            <div className="cta-trust-item">
              <FontAwesomeIcon icon={faClock} />
              <span>{t("createAgentCTA.trust2", "Free test call included")}</span>
            </div>
          </div>
        </div>

        <div className="cta-visual-col">
          <div className="cta-carousel-wrapper">
            <div className="cta-carousel-track">
              {visibleAgents.map((agent, idx) => (
                <div key={`${agent.name}-${idx}`} className={`cta-agent-card ${agent.position}`}>
                  <div className="cta-card-avatar">
                    <img src={agent.image} alt={agent.name} />
                  </div>
                  <div className="cta-card-name">{agent.name}</div>
                  <div className="cta-card-role">{agent.role}</div>
                  {agent.position === "center" && (
                    <>
                      <div className="cta-card-stats">
                        <div className="cta-stat">
                          <div className="cta-stat-value">24/7</div>
                          <div className="cta-stat-label">{t("createAgentCTA.statAvail", "Available")}</div>
                        </div>
                        <div className="cta-stat">
                          <div className="cta-stat-value">0.3s</div>
                          <div className="cta-stat-label">{t("createAgentCTA.statResponse", "Response")}</div>
                        </div>
                        <div className="cta-stat">
                          <div className="cta-stat-value">∞</div>
                          <div className="cta-stat-label">{t("createAgentCTA.statCalls", "Calls")}</div>
                        </div>
                      </div>
                      <div className="cta-card-wave">
                        {[...Array(7)].map((_, i) => (
                          <div key={i} className="wave-bar" />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="cta-carousel-nav">
              <button className="cta-nav-btn" onClick={goPrev} aria-label="Previous agent">
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <div className="cta-carousel-dots">
                {CTA_AGENTS.map((_, idx) => (
                  <div
                    key={idx}
                    className={`cta-dot ${idx === activeIndex ? "active" : ""}`}
                    onClick={() => { setActiveIndex(idx); }}
                  />
                ))}
              </div>
              <button className="cta-nav-btn" onClick={goNext} aria-label="Next agent">
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreateAgentCTA;
